/**
 * Client-side ZIP expansion for the bulk importer.
 *
 * folderUpload.js is deliberately MEMORY-SAFE: it only collects File *handles*
 * and never reads bytes. ZIP import necessarily breaks that invariant — the
 * archive must be decompressed in the browser — so it lives in its own module
 * with explicit guards:
 *
 *   • a hard cap on the (compressed) archive size — rejected up front, before
 *     anything is decompressed;
 *   • streaming, per-entry extraction — exactly one entry is decompressed at a
 *     time (never the whole archive at once), with a running decompressed-byte
 *     guard that aborts a zip bomb mid-stream;
 *   • nested .zip archives are unpacked IN PLACE, treated as a sub-folder named
 *     after the archive (sans ".zip");
 *   • password-protected archives are rejected cleanly (JSZip throws while
 *     parsing the central directory — see zipEntry.js — so this is fail-fast,
 *     before any decompression);
 *   • zip-slip / path-traversal is neutralized (".." and absolute/drive paths
 *     are stripped) so a malicious entry can't escape its folder in the tree or
 *     the materialized `metadata.folderPath`.
 *
 * Entry point: `expandZipEntries(entries, opts)` takes the same
 * `{ file, relativePath }[]` shape every other importer source produces
 * (folderUpload.js) and returns the same shape, with any `.zip` entry replaced
 * by its extracted contents. Everything downstream (tree build, tagging, hash,
 * upload) is byte-source-agnostic and needs no changes.
 */

import JSZip from "jszip";
import { isIgnored } from "./folderUpload";
import { fmtBytes, extensionOf } from "./format";

/**
 * Compressed-archive ceiling — the literal "cap the archive size". A picked or
 * dropped `.zip` larger than this is rejected before extraction (its `size` is
 * known up front). Above this, the user should unzip locally and drag the
 * folder, which stays memory-safe (no in-browser decompression).
 */
export const MAX_ARCHIVE_BYTES = 1024 * 1024 * 1024; // 1 GB compressed

/**
 * Decompressed-total ceiling per top-level archive — a zip-bomb / OOM guard.
 * Tracked as we stream so we abort the moment an archive's expanded bytes blow
 * past it, rather than after fully decompressing it. Reset per top-level
 * archive (nested archives count toward their parent).
 */
export const MAX_UNZIPPED_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB uncompressed

/** Hard ceiling on nested-archive recursion (defends against zip quines). */
export const MAX_ZIP_DEPTH = 8;

/** Typed error so the wizard can show a friendly, code-specific message. */
export class ZipImportError extends Error {
    constructor(code, message) {
        super(message);
        this.name = "ZipImportError";
        // 'too_large' | 'encrypted' | 'corrupt' | 'bomb' | 'too_deep'
        this.code = code;
    }
}

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

/** True if `name` looks like a ZIP archive (the only format we unpack). */
export function isZipName(name) {
    return extensionOf(name) === "zip";
}

/** Drop a trailing ".zip" (case-insensitive) — used to name the unpack folder. */
export function stripZipExt(path) {
    return String(path || "").replace(/\.zip$/i, "");
}

// ZIP entries carry no MIME type; infer one from the extension so previews and
// type facets work. Unknown → "" (downstream falls back to octet-stream).
const MIME_BY_EXT = {
    // audio
    wav: "audio/wav", mp3: "audio/mpeg", ogg: "audio/ogg", flac: "audio/flac",
    aiff: "audio/aiff", aif: "audio/aiff", m4a: "audio/mp4",
    // image
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif",
    bmp: "image/bmp", tiff: "image/tiff", tif: "image/tiff", webp: "image/webp",
    svg: "image/svg+xml", tga: "image/x-tga", exr: "image/x-exr",
    hdr: "image/vnd.radiance", psd: "image/vnd.adobe.photoshop",
    // video
    mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm",
    avi: "video/x-msvideo", mkv: "video/x-matroska",
    // 3D models
    gltf: "model/gltf+json", glb: "model/gltf-binary", obj: "model/obj",
    stl: "model/stl", dae: "model/vnd.collada+xml",
    // documents
    pdf: "application/pdf", txt: "text/plain", md: "text/markdown",
    json: "application/json", xml: "application/xml", csv: "text/csv",
    rtf: "application/rtf", doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    // archives
    zip: "application/zip",
};

/** Best-effort MIME from a filename; "" when unknown. */
export function mimeFromName(name) {
    return MIME_BY_EXT[extensionOf(name)] || "";
}

/**
 * Normalize an in-zip path: convert "\" to "/", and strip empty, ".", ".." and
 * drive-letter ("C:") components so a crafted entry can't traverse out of its
 * folder (zip-slip) when it lands in the tree / `metadata.folderPath`.
 */
export function sanitizeZipPath(path) {
    const out = [];
    for (let part of String(path || "").replace(/\\/g, "/").split("/")) {
        part = part.trim();
        if (!part || part === "." || part === "..") continue;
        if (/^[a-zA-Z]:$/.test(part)) continue; // drive letter (e.g. "C:")
        out.push(part);
    }
    return out.join("/");
}

/** Join two already-sanitized path segments. */
function joinPath(prefix, sub) {
    return [prefix, sub].filter(Boolean).join("/");
}

/** Translate a JSZip load failure into a typed ZipImportError. */
export function mapLoadError(err, name) {
    const msg = (err && err.message) || "";
    if (/encrypt/i.test(msg)) {
        return new ZipImportError(
            "encrypted",
            `“${name}” is password-protected. Password-protected zips can’t be unpacked — remove the password and try again.`
        );
    }
    return new ZipImportError(
        "corrupt",
        `“${name}” couldn’t be read as a zip archive (it may be corrupt or not a real .zip).`
    );
}

/** Does this entry list contain at least one `.zip` to expand? */
export function hasZipEntries(entries) {
    return (Array.isArray(entries) ? entries : []).some(
        (e) => e && e.file && isZipName(e.relativePath || (e.file && e.file.name) || "")
    );
}

// ── Extraction ──────────────────────────────────────────────────────────────

/**
 * Decompress one archive into a flat `{ file, relativePath }[]`, recursing into
 * nested zips. Streams entry-by-entry to bound peak memory and report progress.
 *
 * @param source   File | Blob | Uint8Array — the archive bytes (JSZip-loadable)
 * @param name     display name (for error messages)
 * @param prefix   sanitized path prepended to every extracted entry
 * @param opts     { onProgress?, maxArchiveBytes?, maxUnzippedBytes?, maxDepth? }
 * @param depth    nesting depth (0 = top-level dropped/picked archive)
 * @param bomb     per-top-level-archive decompressed-byte accumulator { bytes }
 * @param progress shared cross-archive file counter { files }
 */
async function extractArchive(source, name, prefix, opts, depth, bomb, progress) {
    const maxArchive = opts.maxArchiveBytes ?? MAX_ARCHIVE_BYTES;
    const maxUnzipped = opts.maxUnzippedBytes ?? MAX_UNZIPPED_BYTES;
    const maxDepth = opts.maxDepth ?? MAX_ZIP_DEPTH;

    if (depth > maxDepth) {
        throw new ZipImportError(
            "too_deep",
            `“${name}” nests archives more than ${maxDepth} levels deep — refusing to unpack.`
        );
    }

    // Compressed-size cap applies to the top-level archive only (the file the
    // user actually dropped). Nested archives are bounded by the bomb guard.
    const size = (source && (source.size ?? source.byteLength)) || 0;
    if (depth === 0 && size > maxArchive) {
        throw new ZipImportError(
            "too_large",
            `“${name}” is ${fmtBytes(size)} — larger than the ${fmtBytes(maxArchive)} zip limit. Unzip it locally and drag the folder instead.`
        );
    }

    let zip;
    try {
        zip = await JSZip.loadAsync(source); // throws on encrypted (central-dir) + corrupt
    } catch (err) {
        if (err instanceof ZipImportError) throw err;
        throw mapLoadError(err, name);
    }

    // Snapshot entries first; skip directory markers (recreated from paths).
    const objects = [];
    zip.forEach((path, obj) => {
        if (!obj.dir) objects.push({ path, obj });
    });

    // Pre-pass: grow the running decompressed-byte total by this archive's
    // surviving leaf entries (using the trustworthy central-directory size) so
    // progress can be reported as a determinate fraction. Nested archives are
    // skipped here — they add their own leaves when they're opened — and junk /
    // zip-slip entries are excluded so the denominator matches what we'll emit.
    for (const { path, obj } of objects) {
        const cleanPath = sanitizeZipPath(path);
        if (!cleanPath || cleanPath.split("/").some(isIgnored)) continue;
        if (isZipName(cleanPath.split("/").pop())) continue;
        const declared = obj._data && obj._data.uncompressedSize;
        if (typeof declared === "number") progress.totalBytes += declared;
    }

    const out = [];
    for (const { path, obj } of objects) {
        // Cooperative cancellation: stop decompressing further entries when the
        // caller aborts (e.g. the wizard unmounts mid-unpack). The in-flight
        // entry can't be interrupted, but we don't start the next one.
        if (opts.signal && opts.signal.aborted) {
            throw new ZipImportError("aborted", "Extraction was cancelled.");
        }

        const clean = sanitizeZipPath(path);
        if (!clean) continue;
        // Drop OS/junk artifacts by ANY path component (e.g. "__MACOSX/…").
        if (clean.split("/").some(isIgnored)) continue;

        const base = clean.split("/").pop();
        const fullPath = joinPath(prefix, clean);

        // Zip-bomb guard #1 — refuse BEFORE decompressing if this entry's
        // declared uncompressed size alone would blow the cap, so a single huge
        // entry can't OOM the tab inside the async() allocation below. JSZip
        // validates the declared size against the actual inflated length (it
        // throws on a mismatch), so this central-directory value is trustworthy.
        const declared = obj._data && obj._data.uncompressedSize;
        if (typeof declared === "number" && bomb.bytes + declared > maxUnzipped) {
            throw new ZipImportError(
                "bomb",
                `“${name}” expands to more than ${fmtBytes(maxUnzipped)} — refusing to unpack (possible zip bomb).`
            );
        }

        let data;
        try {
            // eslint-disable-next-line no-await-in-loop -- streaming by design:
            // one entry decompressed at a time keeps peak memory bounded.
            data = await obj.async("uint8array");
        } catch (err) {
            if (err instanceof ZipImportError) throw err;
            throw new ZipImportError(
                "corrupt",
                `“${name}” couldn’t be unpacked — entry “${base}” is corrupt or unreadable.`
            );
        }

        // Zip-bomb guard #2 — belt-and-suspenders on the ACTUAL inflated bytes
        // (covers archives whose central directory omits/understates the size).
        bomb.bytes += data.length;
        if (bomb.bytes > maxUnzipped) {
            throw new ZipImportError(
                "bomb",
                `“${name}” expands to more than ${fmtBytes(maxUnzipped)} — refusing to unpack (possible zip bomb).`
            );
        }

        if (isZipName(base)) {
            // Nested archive → unpack in place under a folder named after it.
            // Sanitize after stripping ".zip" (see expandZipEntries) so a name
            // like "...zip" can't reintroduce a ".." segment into the prefix.
            // eslint-disable-next-line no-await-in-loop
            const nested = await extractArchive(
                data,
                base,
                sanitizeZipPath(stripZipExt(fullPath)),
                opts,
                depth + 1,
                bomb,
                progress
            );
            out.push(...nested);
        } else {
            const file = new File([data], base, { type: mimeFromName(base) });
            out.push({ file, relativePath: fullPath });
            progress.files += 1;
            progress.bytes += data.length;
            if (opts.onProgress) opts.onProgress({ ...progress });
        }
    }

    return out;
}

/**
 * Expand any `.zip` entries in an importer selection in place. Non-zip entries
 * pass through untouched; a selection with no archives is returned as-is (same
 * reference) so the common folder/file path pays nothing.
 *
 * Throws a {@link ZipImportError} (with a `.code`) on a too-large, encrypted,
 * corrupt, bomb, or too-deeply-nested archive — the caller surfaces the message.
 *
 * @param {Array<{file: File, relativePath: string}>} entries
 * @param {{onProgress?:(p:{files:number,bytes:number,totalBytes:number})=>void, signal?:AbortSignal, maxArchiveBytes?:number, maxUnzippedBytes?:number, maxDepth?:number}} [opts]
 *   `onProgress` is called per extracted file with a snapshot of the running
 *   `{ files, bytes, totalBytes }` — `totalBytes` is the declared decompressed
 *   size discovered so far (0 if the archive omits sizes; treat as indeterminate).
 *   `signal` lets the caller abort a long unpack between entries (throws a
 *   ZipImportError with code "aborted").
 * @returns {Promise<Array<{file: File, relativePath: string}>>}
 */
export async function expandZipEntries(entries, opts = {}) {
    const list = Array.isArray(entries) ? entries : [];
    if (!hasZipEntries(list)) return list;

    const progress = { files: 0, bytes: 0, totalBytes: 0 };
    const out = [];
    for (const entry of list) {
        const rel = (entry && entry.relativePath) || (entry && entry.file && entry.file.name) || "";
        if (entry && entry.file && isZipName(rel)) {
            // Strip ".zip" BEFORE sanitizing: stripping can expose dot segments
            // (e.g. "...zip" → ".."), which sanitize must then remove — doing it
            // in the other order would let a "." / ".." prefix slip through.
            const prefix = sanitizeZipPath(stripZipExt(rel)) || "archive";
            const displayName = sanitizeZipPath(rel).split("/").pop() || "archive.zip";
            // eslint-disable-next-line no-await-in-loop -- sequential keeps only
            // one archive's transient decompression buffers live at a time.
            const extracted = await extractArchive(entry.file, displayName, prefix, opts, 0, { bytes: 0 }, progress);
            out.push(...extracted);
        } else {
            out.push(entry);
        }
    }
    return out;
}
